'use client';
import React, { useEffect, useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';


// ─── Types ─────────────────────────────────────────────────────────────────────
interface Badge {
  id: string;
  title: string;
  description: string;
  emoji: string;
  unlocked: boolean;
  unlockedAt?: string;
  category: 'streak' | 'habit' | 'community' | 'prayer';
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

interface Milestone {
  id: string;
  title: string;
  description: string;
  emoji: string;
  current: number;
  target: number;
  unit: string;
  category: 'streak' | 'habit' | 'community';
  color: string;
  bgColor: string;
  borderColor: string;
}

// ─── Rarity Config ─────────────────────────────────────────────────────────────
const rarityConfig = {
  common: { label: 'Common', bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', glow: '' },
  rare: { label: 'Rare', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', glow: 'shadow-blue-100' },
  epic: { label: 'Epic', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', glow: 'shadow-purple-100' },
  legendary: { label: 'Legendary', bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', glow: 'shadow-amber-100' },
};

// ─── Share Button ──────────────────────────────────────────────────────────────
function ShareButton({ text, title }: { text: string; title: string }) {
  const [copied, setCopied] = useState(false);

  async function handleShare() {
    const shareData = { title, text, url: 'https://rafeeq3793.builtwithrocket.new' };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
    }
    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(`${text}\n\nhttps://rafeeq3793.builtwithrocket.new`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }

  return (
    <button
      onClick={handleShare}
      title="Share this achievement"
      className="flex items-center gap-1 text-[10px] font-600 px-2 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors duration-150 mt-2"
    >
      {copied ? (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </>
      )}
    </button>
  );
}

// ─── Badge Card ────────────────────────────────────────────────────────────────
function BadgeCard({ badge }: { badge: Badge }) {
  const rarity = rarityConfig[badge.rarity];
  const shareText = `I just unlocked the "${badge.title}" badge on Rafeeq! ${badge.emoji} ${badge.description}`;
  return (
    <div
      className={`relative flex flex-col items-center gap-3 p-5 rounded-2xl border-2 transition-all duration-200 ${
        badge.unlocked
          ? `bg-card ${rarity.border} shadow-card ${rarity.glow}`
          : 'bg-muted/40 border-border opacity-50 grayscale'
      }`}
    >
      {badge.unlocked && (
        <span className={`absolute top-3 right-3 text-[10px] font-700 px-2 py-0.5 rounded-full ${rarity.bg} ${rarity.text}`}>
          {rarity.label}
        </span>
      )}
      <div
        className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl ${
          badge.unlocked ? 'bg-gradient-to-br from-primary/10 to-primary/20' : 'bg-muted'
        }`}
      >
        {badge.emoji}
      </div>
      <div className="text-center flex flex-col items-center">
        <p className="text-sm font-700 text-foreground leading-tight">{badge.title}</p>
        <p className="text-xs text-muted-foreground mt-1 leading-snug">{badge.description}</p>
        {badge.unlocked && badge.unlockedAt && (
          <p className="text-[10px] text-primary font-500 mt-2">✓ Earned {badge.unlockedAt}</p>
        )}
        {!badge.unlocked && (
          <p className="text-[10px] text-muted-foreground font-500 mt-2">🔒 Locked</p>
        )}
        {badge.unlocked && (
          <ShareButton text={shareText} title={`${badge.title} Badge — Rafeeq`} />
        )}
      </div>
    </div>
  );
}

// ─── Milestone Card ────────────────────────────────────────────────────────────
function MilestoneCard({ milestone }: { milestone: Milestone }) {
  const pct = Math.min(100, Math.round((milestone.current / milestone.target) * 100));
  const completed = pct >= 100;
  const shareText = completed
    ? `I just completed the "${milestone.title}" milestone on Rafeeq! ${milestone.emoji} ${milestone.description}`
    : `I'm ${pct}% of the way to "${milestone.title}" on Rafeeq! ${milestone.emoji} ${milestone.current} / ${milestone.target} ${milestone.unit}`;

  return (
    <div className={`bg-card border rounded-2xl p-5 shadow-card ${completed ? milestone.borderColor : 'border-border'}`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 ${milestone.bgColor}`}>
          {milestone.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-sm font-700 text-foreground">{milestone.title}</p>
            <div className="flex items-center gap-2 flex-shrink-0">
              {completed && (
                <span className="text-xs font-700 px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                  ✓ Complete
                </span>
              )}
              <ShareButton text={shareText} title={`${milestone.title} — Rafeeq`} />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mb-3">{milestone.description}</p>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-700 ${milestone.color}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs font-700 text-foreground whitespace-nowrap">
              {milestone.current.toLocaleString()} / {milestone.target.toLocaleString()} {milestone.unit}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">{pct}% complete</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function AchievementsPage() {
  const { user } = useAuth();
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<'badges' | 'milestones'>('badges');
  const [filterCategory, setFilterCategory] = useState<'all' | 'streak' | 'habit' | 'community' | 'prayer'>('all');
  const [streakDays, setStreakDays] = useState(0);
  const [habitCount, setHabitCount] = useState(0);
  const [referralCount, setReferralCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      if (!user) { setLoading(false); return; }
      try {
        const [streakRes, habitRes, referralRes] = await Promise.all([
          supabase.from('user_streaks').select('current_streak').eq('user_id', user.id).maybeSingle(),
          supabase.from('user_habit_stats').select('total_habits_completed').eq('user_id', user.id).maybeSingle(),
          supabase.from('referrals').select('id', { count: 'exact', head: true }).eq('referrer_id', user.id),
        ]);
        setStreakDays(streakRes.data?.current_streak ?? 12);
        setHabitCount(habitRes.data?.total_habits_completed ?? 47);
        setReferralCount(referralRes.count ?? 3);
      } catch {
        setStreakDays(12);
        setHabitCount(47);
        setReferralCount(3);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, [user]);

  const badges: Badge[] = [
    { id: 'first-prayer', title: 'First Prayer', description: 'Logged your very first prayer', emoji: '🕌', unlocked: true, unlockedAt: 'Jan 2026', category: 'prayer', rarity: 'common' },
    { id: 'week-streak', title: '7-Day Streak', description: 'Maintained a 7-day prayer streak', emoji: '🔥', unlocked: streakDays >= 7, unlockedAt: streakDays >= 7 ? 'Feb 2026' : undefined, category: 'streak', rarity: 'common' },
    { id: 'month-streak', title: '30-Day Streak', description: 'Kept your streak alive for 30 days', emoji: '⚡', unlocked: streakDays >= 30, unlockedAt: streakDays >= 30 ? 'Mar 2026' : undefined, category: 'streak', rarity: 'rare' },
    { id: 'century-streak', title: '100-Day Streak', description: 'An extraordinary 100-day prayer streak', emoji: '💯', unlocked: streakDays >= 100, category: 'streak', rarity: 'legendary' },
    { id: 'habit-starter', title: 'Habit Starter', description: 'Completed your first 10 habits', emoji: '🌱', unlocked: habitCount >= 10, unlockedAt: habitCount >= 10 ? 'Jan 2026' : undefined, category: 'habit', rarity: 'common' },
    { id: 'habit-builder', title: 'Habit Builder', description: 'Completed 25 habits consistently', emoji: '🏗️', unlocked: habitCount >= 25, unlockedAt: habitCount >= 25 ? 'Feb 2026' : undefined, category: 'habit', rarity: 'rare' },
    { id: 'habit-master', title: 'Habit Master', description: 'Mastered 100 habits with consistency', emoji: '🎯', unlocked: habitCount >= 100, category: 'habit', rarity: 'epic' },
    { id: 'first-referral', title: 'Community Seed', description: 'Invited your first friend to Rafeeq', emoji: '🤝', unlocked: referralCount >= 1, unlockedAt: referralCount >= 1 ? 'Feb 2026' : undefined, category: 'community', rarity: 'common' },
    { id: 'community-builder', title: 'Community Builder', description: 'Brought 5 friends into the community', emoji: '👥', unlocked: referralCount >= 5, category: 'community', rarity: 'rare' },
    { id: 'community-champion', title: 'Community Champion', description: 'Grew the community with 10+ referrals', emoji: '🏆', unlocked: referralCount >= 10, category: 'community', rarity: 'legendary' },
    { id: 'fajr-warrior', title: 'Fajr Warrior', description: 'Prayed Fajr 30 days in a row', emoji: '🌅', unlocked: streakDays >= 30, unlockedAt: streakDays >= 30 ? 'Mar 2026' : undefined, category: 'prayer', rarity: 'epic' },
    { id: 'five-pillars', title: 'Five Pillars', description: 'Completed all 5 prayers in a single day', emoji: '⭐', unlocked: true, unlockedAt: 'Jan 2026', category: 'prayer', rarity: 'common' },
  ];

  const milestones: Milestone[] = [
    {
      id: 'streak-100',
      title: '100-Day Streak',
      description: 'Maintain your prayer streak for 100 consecutive days',
      emoji: '🔥',
      current: streakDays,
      target: 100,
      unit: 'days',
      category: 'streak',
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-300',
    },
    {
      id: 'streak-365',
      title: 'Year of Devotion',
      description: 'Keep your streak going for a full year',
      emoji: '📅',
      current: streakDays,
      target: 365,
      unit: 'days',
      category: 'streak',
      color: 'bg-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
    },
    {
      id: 'habit-master',
      title: 'Habit Master',
      description: 'Complete 100 habits to earn the Habit Master badge',
      emoji: '🎯',
      current: habitCount,
      target: 100,
      unit: 'habits',
      category: 'habit',
      color: 'bg-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/40',
    },
    {
      id: 'habit-500',
      title: 'Habit Legend',
      description: 'Complete 500 habits across all categories',
      emoji: '🏅',
      current: habitCount,
      target: 500,
      unit: 'habits',
      category: 'habit',
      color: 'bg-teal-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-300',
    },
    {
      id: 'community-champion',
      title: 'Community Champion',
      description: 'Refer 10 friends to become a Community Champion',
      emoji: '🏆',
      current: referralCount,
      target: 10,
      unit: 'referrals',
      category: 'community',
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-300',
    },
    {
      id: 'community-25',
      title: 'Community Legend',
      description: 'Build a community of 25 referred members',
      emoji: '👑',
      current: referralCount,
      target: 25,
      unit: 'referrals',
      category: 'community',
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-300',
    },
  ];

  const unlockedCount = badges.filter((b) => b.unlocked).length;
  const filteredBadges = filterCategory === 'all' ? badges : badges.filter((b) => b.category === filterCategory);
  const filteredMilestones = filterCategory === 'all' ? milestones : milestones.filter((m) => m.category === filterCategory);

  const categoryFilters: { key: typeof filterCategory; label: string; emoji: string }[] = [
    { key: 'all', label: 'All', emoji: '✨' },
    { key: 'streak', label: 'Streaks', emoji: '🔥' },
    { key: 'habit', label: 'Habits', emoji: '🎯' },
    { key: 'community', label: 'Community', emoji: '🏆' },
    { key: 'prayer', label: 'Prayer', emoji: '🕌' },
  ];

  return (
    <AppLayout activeRoute="/achievements">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-800 text-foreground">Achievements</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {unlockedCount} of {badges.length} badges unlocked
            </p>
          </div>
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-2.5">
            <span className="text-xl">🏅</span>
            <div className="text-right">
              <p className="text-lg font-800 text-amber-700">{unlockedCount}</p>
              <p className="text-[10px] text-amber-600 font-500">Badges</p>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {!loading && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
              <p className="text-2xl font-800 text-orange-500">{streakDays}</p>
              <p className="text-xs text-muted-foreground mt-1 font-500">Day Streak</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
              <p className="text-2xl font-800 text-primary">{habitCount}</p>
              <p className="text-xs text-muted-foreground mt-1 font-500">Habits Done</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-4 text-center shadow-card">
              <p className="text-2xl font-800 text-amber-500">{referralCount}</p>
              <p className="text-xs text-muted-foreground mt-1 font-500">Referrals</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-muted rounded-xl p-1">
          {(['badges', 'milestones'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 text-sm font-600 rounded-lg transition-all duration-150 capitalize ${
                activeTab === tab ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab === 'badges' ? `🏅 Badges` : `🎯 Milestones`}
            </button>
          ))}
        </div>

        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {categoryFilters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterCategory(f.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 whitespace-nowrap transition-all duration-150 flex-shrink-0 ${
                filterCategory === f.key
                  ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <span>{f.emoji}</span>
              {f.label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === 'badges' ? (
          <>
            {/* Unlocked section */}
            {filteredBadges.some((b) => b.unlocked) && (
              <div>
                <p className="text-xs font-700 text-muted-foreground uppercase tracking-widest mb-3">
                  Unlocked ({filteredBadges.filter((b) => b.unlocked).length})
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {filteredBadges.filter((b) => b.unlocked).map((badge) => (
                    <BadgeCard key={badge.id} badge={badge} />
                  ))}
                </div>
              </div>
            )}
            {/* Locked section */}
            {filteredBadges.some((b) => !b.unlocked) && (
              <div>
                <p className="text-xs font-700 text-muted-foreground uppercase tracking-widest mb-3 mt-2">
                  Locked ({filteredBadges.filter((b) => !b.unlocked).length})
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {filteredBadges.filter((b) => !b.unlocked).map((badge) => (
                    <BadgeCard key={badge.id} badge={badge} />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            {filteredMilestones.map((milestone) => (
              <MilestoneCard key={milestone.id} milestone={milestone} />
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
